import { createTheme, ThemeOptions } from "@mui/material/styles";

import { themeColors } from "./colorTokens";

/**
 * Cellera MUI Theme Configuration
 * Integrates our color tokens with Material-UI theming system
 */
export const celleraThemeOptions: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      main: themeColors.primary.main,
      dark: themeColors.primary.dark,
      light: themeColors.primary.light,
      contrastText: themeColors.primary.contrast,
    },
    secondary: {
      main: themeColors.secondary.main,
      dark: themeColors.secondary.dark,
      light: themeColors.secondary.light,
      contrastText: themeColors.secondary.contrast,
    },
    text: {
      primary: themeColors.text.primary,
      secondary: themeColors.text.secondary,
      disabled: themeColors.text.disabled,
    },
    background: {
      default: themeColors.background.default,
      paper: themeColors.background.paper,
    },
    success: {
      main: themeColors.success[500],
      light: themeColors.success[100],
      dark: themeColors.success[700],
    },
    warning: {
      main: themeColors.warning[500],
      light: themeColors.warning[100],
      dark: themeColors.warning[700],
    },
    error: {
      main: themeColors.error[500],
      light: themeColors.error[100],
      dark: themeColors.error[700],
    },
    info: {
      main: themeColors.info[500],
      light: themeColors.info[100],
      dark: themeColors.info[700],
    },
  },
  typography: {
    fontFamily: "RedHatDisplay, -apple-system, BlinkMacSystemFont, sans-serif",
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
      color: themeColors.text.primary,
    },
    h2: {
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.3,
      color: themeColors.text.primary,
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.3,
      color: themeColors.text.primary,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
      color: themeColors.text.primary,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
      color: themeColors.text.primary,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.4,
      color: themeColors.text.primary,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.5,
      color: themeColors.text.primary,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
      color: themeColors.text.secondary,
    },
    button: {
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: "0.02em",
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          textTransform: "none",
          padding: "12px 24px",
          fontSize: "1rem",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
          },
        },
        contained: {
          backgroundColor: themeColors.primary.main,
          color: themeColors.primary.contrast,
          "&:hover": {
            backgroundColor: themeColors.primary.dark,
          },
        },
        outlined: {
          borderColor: themeColors.border.main,
          color: themeColors.border.main,
          "&:hover": {
            borderColor: themeColors.primary.main,
            backgroundColor: themeColors.primary.light,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            backgroundColor: themeColors.background.paper,
            "& fieldset": {
              borderColor: themeColors.border.light,
            },
            "&:hover fieldset": {
              borderColor: themeColors.border.main,
            },
            "&.Mui-focused fieldset": {
              borderColor: themeColors.primary.main,
              borderWidth: "2px",
            },
            "&.Mui-error fieldset": {
              borderColor: themeColors.error[700],
              borderWidth: "2px",
            },
            "&.Mui-error:hover fieldset": {
              borderColor: themeColors.error[700],
              borderWidth: "2px",
            },
            "&.Mui-error.Mui-focused fieldset": {
              borderColor: themeColors.error[700],
              borderWidth: "2px",
            },
          },
          "& .MuiInputLabel-root": {
            color: themeColors.text.secondary,
            "&.Mui-focused": {
              color: themeColors.primary.main,
            },
            "&.Mui-error": {
              color: themeColors.error[700],
            },
            "&.Mui-error.Mui-focused": {
              color: themeColors.error[700],
            },
          },
          "& .MuiFormHelperText-root": {
            "&.Mui-error": {
              color: themeColors.error[700],
              fontWeight: 500,
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: themeColors.info[50],
          color: themeColors.info[700],
          "& .MuiAlert-icon": {
            color: themeColors.info[600],
          },
        },
        standardSuccess: {
          backgroundColor: themeColors.success[50],
          color: themeColors.success[700],
          "& .MuiAlert-icon": {
            color: themeColors.success[600],
          },
        },
        standardWarning: {
          backgroundColor: themeColors.warning[50],
          color: themeColors.warning[700],
          "& .MuiAlert-icon": {
            color: themeColors.warning[600],
          },
        },
        standardError: {
          backgroundColor: themeColors.error[50],
          color: themeColors.error[700],
          "& .MuiAlert-icon": {
            color: themeColors.error[600],
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: themeColors.background.paper,
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
        },
      },
    },
  },
};

// Create the theme instance
export const celleraTheme = createTheme(celleraThemeOptions);

// Export theme for use in ThemeProvider
export default celleraTheme;
